import { Lightning, Registry } from '@lightningjs/sdk';
import Carousel, {
  CarouselTemplateSpec,
} from 'components/common/carousels/Carousel';
import { LiveEventLaneItem } from 'services/cwData';
import { getLiveEventStatus } from 'support/contentUtils';
import { LiveEventStream } from 'types/api/media';
import { constants } from 'aliases';
import { LiveEventTileTemplateSpec } from 'components/common/tiles/LiveEventTile';

export interface LiveEventCarouselTemplateSpec extends CarouselTemplateSpec {
  laneItems: (LiveEventLaneItem | LiveEventStream)[];
}

const MAX_TIMEOUT = constants.timers.timeoutMaximum;
const REFRESH_TIME_OFFSET = 1000;

export class LiveEventCarousel
  extends Carousel<LiveEventCarouselTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<LiveEventCarouselTemplateSpec>
{
  override readonly _imageKey = 'eventThumbnail';
  override readonly _expandedImageKey = null;

  // Internal
  private liveEventTimeout: number | null = null;

  override set laneItems(
    laneItems: LiveEventCarouselTemplateSpec['laneItems'],
  ) {
    this.stopUpdateTime();
    super.laneItems = laneItems;
  }

  override _inactive() {
    super._inactive();

    this.stopUpdateTime();
  }

  protected override buildTiles(
    laneItems: LiveEventCarouselTemplateSpec['laneItems'],
  ) {
    let timeout = MAX_TIMEOUT;
    let tileCount = -1;

    const tiles = laneItems
      .map(liveEvent => {
        const { eventStatus, availabilityChangeDate } =
          getLiveEventStatus(liveEvent);

        if (!availabilityChangeDate) {
          // Live event is no longer available
          return null;
        }

        const refreshDate =
          availabilityChangeDate.getTime() - Date.now() + REFRESH_TIME_OFFSET;
        if (refreshDate > 0) timeout = Math.min(timeout, refreshDate);

        if (!eventStatus) {
          // Live event is not available yet
          return null;
        } else {
          // Live event is available
          tileCount += 1;
          return this.buildTile(liveEvent, tileCount);
        }
      })
      .filter(
        item => item !== null,
      ) as Lightning.Element.PatchTemplate<LiveEventTileTemplateSpec>[];

    this.liveEventTimeout = Registry.setTimeout(
      this.updateCarousel.bind(this),
      timeout,
    );

    return tiles;
  }

  private stopUpdateTime() {
    const liveEventTimeout = this.liveEventTimeout;
    if (!liveEventTimeout) return;

    Registry.clearTimeout(liveEventTimeout);
    this.liveEventTimeout = null;
  }
}
